import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAdminCreateMarker, useGetGatheringSpotsAll } from '@outdoria/api';
import { Marker } from '@outdoria/types';
import { Button, Loader, QueryBoundary } from '@outdoria/ui';
import { ActionBar } from '../../../containers';
import { Form } from '../../../components';
import { useForm } from '../../../hooks';

import s from './styles.module.scss';

const DEFAULT_MARKER: Marker = {
  marker_id: '',
  gathering_spot_id: null,
  coords_x: 0,
  coords_y: 0,
};

type MarkerProps = {
  selectedCoords: [number, number];
  onExit: () => void;
};
export const MarkerCreate = (props: MarkerProps) => (
  <QueryBoundary fallback={<Loader />}>
    <Load {...props} />
  </QueryBoundary>
);

const Load = ({ selectedCoords, onExit }: MarkerProps) => {
  const { mutate, isSuccess } = useCreate();
  const { gatheringSpotOptions } = useOptions();
  const { register, form, onReset } = useForm<Marker>({
    ...DEFAULT_MARKER,
    coords_x: selectedCoords[0],
    coords_y: selectedCoords[1],
  });

  const onClearState = () => {
    onReset();
    if (onExit) onExit();
  };

  const onCreate = () => {
    mutate(form);
  };

  useEffect(() => {
    if (isSuccess) {
      onClearState();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (selectedCoords[0] !== form.coords_x) {
      register('coords_x').setValue(selectedCoords[0]);
    }
    if (selectedCoords[1] !== form.coords_y) {
      register('coords_y').setValue(selectedCoords[1]);
    }
  }, [selectedCoords]);

  return (
    <>
      <ActionBar
        buttons={
          <>
            <Button
              label="Cancel"
              inverted
              simple
              variant={Button.Variant.GHOST}
              onClick={onClearState}
            />
            <Button
              label="Save new marker"
              variant={Button.Variant.ACTION}
              onClick={onCreate}
            />
          </>
        }
      />

      <div className={s.formContainer}>
        <Form pretty>
          <Form.Input
            label="Latitude"
            {...register('coords_x', { required: true })}
          />

          <Form.Input
            label="Longtitude"
            {...register('coords_y', { required: true })}
          />

          <Form.Select
            label="Gathering spot"
            data={gatheringSpotOptions}
            {...register('gathering_spot_id', { required: true })}
          />
        </Form>
      </div>
    </>
  );
};

const useOptions = () => {
  const { data: gatheringSpots } = useGetGatheringSpotsAll();
  const gatheringSpotOptions = gatheringSpots.map(spot => ({
    value: spot.gathering_spot_id,
    label: spot.name,
  }));

  return { gatheringSpotOptions };
};

const useCreate = () => {
  const { mutate, error, isSuccess } = useAdminCreateMarker();

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Marker created :3`);
    }
  }, [isSuccess]);

  return { mutate, error, isSuccess };
};
